import type { CartItem, DiscountItem } from "./cartContextTypes";

type CartTotals = {
  subTotal: number;
  tax: number;
  taxLines: { name: string; ratePercent?: number | null; amount: number }[];
  grandTotal: number;
  depositTotal: number;
  customerFee: number;
};

/**
 * The cart is priced for one business at full price. Pro-ration and
 * multi-business pricing scale only the membership row (`ratio`); add-ons stay
 * at face value. Discounts are re-applied to that base the way
 * `renewableFeeInitiate` charges them: percentages follow the new base, fixed
 * amounts stay fixed.
 */
export function rescaleRenewableCartTotals({
  cart,
  ratio,
  totals,
  discounts,
  percentageDiscountIds,
}: {
  cart: Pick<CartItem, "total">[];
  ratio: number;
  totals: CartTotals;
  discounts: DiscountItem[];
  percentageDiscountIds: Set<string>;
}): { adjustedTotals: CartTotals; adjustedDiscounts: DiscountItem[] } {
  if (ratio === 1) return { adjustedTotals: totals, adjustedDiscounts: discounts };

  const membershipTotal = cart[0]?.total ?? 0;
  const addOnsTotal = cart.slice(1).reduce((sum, item) => sum + item.total, 0);
  const cartBase = membershipTotal + addOnsTotal;
  const base = Math.round(membershipTotal * ratio) + addOnsTotal;

  let remaining = base;
  const adjustedDiscounts = discounts.map(discount => {
    const scaled =
      percentageDiscountIds.has(String(discount.discountId)) && cartBase > 0
        ? Math.round((discount.total * base) / cartBase)
        : discount.total;
    const total = Math.min(remaining, scaled);
    remaining -= total;
    return { ...discount, total };
  });

  const subTotal = remaining;
  const subTotalRatio = totals.subTotal > 0 ? subTotal / totals.subTotal : ratio;
  const taxLines = totals.taxLines.map(line => ({
    ...line,
    amount: Math.round(line.amount * subTotalRatio),
  }));
  const tax = taxLines.reduce((sum, line) => sum + line.amount, 0);
  const customerFee = Math.round(totals.customerFee * subTotalRatio);
  const grandTotal = subTotal + tax + customerFee;
  return {
    adjustedDiscounts,
    adjustedTotals: {
      subTotal,
      tax,
      taxLines,
      grandTotal,
      depositTotal:
        totals.grandTotal > 0
          ? Math.round((totals.depositTotal * grandTotal) / totals.grandTotal)
          : 0,
      customerFee,
    },
  };
}
